// Compactación del contexto del Chat — ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar.
//
// El historial que viaja a la API se rearma en CADA turno desde `messages` y sólo crece:
// texto del usuario, texto del agente y los `[registro: …]` de todo lo que usó. En un chat
// largo eso terminaba en "prompt is too long" y el turno se perdía entero
// (ADR-2026-09-12-el-chat-dice-por-que-fallo-el-turno lo explicaba, pero no lo evitaba).
//
// Acá vive la parte PURA de la solución: medir, elegir dónde cortar y pegar el resumen.
// Quién escribe el resumen (Gemini, en el backend) vive en `compactarApi.ts`; cuándo se
// dispara, en `useV2Chat`.
//
// Dos reglas que ordenan todo el módulo:
//  1. Se compacta lo que se ENVÍA, nunca lo que se guarda. `messages` queda completo —
//     el usuario sigue viendo su chat entero y `buscar_en_el_chat` sigue teniendo dónde
//     buscar. Sin esto, resumir sería borrar.
//  2. El corte cae SIEMPRE en un mensaje del usuario. La cola tiene que empezar por `user`
//     (lo pide la API) y cortar en el medio de un intercambio deja al agente contestando
//     algo que ya no está.

export interface MensajeApi {
  role: 'user' | 'assistant';
  content: any;
}

/** Español ≈ 3,6 caracteres por token. Se subestima a propósito el divisor (o sea, se
 *  SOBRE-estiman los tokens): equivocarse para arriba compacta un poco antes, y
 *  equivocarse para abajo pierde el turno. */
export const CHARS_POR_TOKEN = 3.6;

/** Una imagen en el contenido de un mensaje. No hay forma de medirla desde acá (viaja por
 *  URL), así que se cuenta un valor plano del orden de una foto de catálogo. */
export const TOKENS_POR_IMAGEN = 1_600;

/** Ventana del modelo del turno. Gemini (nivel "Baja") tiene 1M; GPT-6 Sol (nivel "Media",
 *  ADR-2026-09-26-media-pasa-a-gpt-6-sol-y-baja-a-gemini-3-8) acepta hasta 922k de entrada;
 *  los Claude, 200k. */
export function ventanaDelModelo(model: string | null | undefined): number {
  const m = (model ?? '').toLowerCase();
  if (m.includes('gemini')) return 1_000_000;
  if (m.startsWith('gpt-6')) return 900_000;
  return 200_000;
}

/** A partir de acá se compacta. No es 0,9 de la ventana porque además del historial viajan
 *  el system prompt, el catálogo de tools y —dentro del turno— los `tool_result` con las
 *  imágenes de lo que se generó, que pueden sumar decenas de miles de tokens. */
export const FRACCION_UMBRAL = 0.45;

/** Cuánto de la ventana se le deja a la cola textual después de compactar. */
export const FRACCION_COLA = 0.2;

/** Aunque el presupuesto diera para menos, se intenta dejar esto de conversación literal:
 *  un resumen es un buen mapa del pasado y un pésimo reemplazo de lo último que se dijo. */
export const MIN_MENSAJES_EN_COLA = 8;

export function estimarTokensDeContenido(content: unknown): number {
  if (typeof content === 'string') return Math.ceil(content.length / CHARS_POR_TOKEN);
  if (Array.isArray(content)) {
    let total = 0;
    for (const b of content) {
      if (!b || typeof b !== 'object') {
        total += estimarTokensDeContenido(b);
        continue;
      }
      const bloque = b as Record<string, unknown>;
      if (bloque.type === 'image') {
        total += TOKENS_POR_IMAGEN;
      } else if (typeof bloque.text === 'string') {
        total += Math.ceil(bloque.text.length / CHARS_POR_TOKEN);
      } else if (bloque.type === 'tool_result') {
        total += estimarTokensDeContenido(bloque.content);
      } else {
        total += Math.ceil(JSON.stringify(bloque).length / CHARS_POR_TOKEN);
      }
    }
    return total;
  }
  if (content == null) return 0;
  return Math.ceil(JSON.stringify(content).length / CHARS_POR_TOKEN);
}

export function estimarTokens(mensajes: MensajeApi[]): number {
  let total = 0;
  for (const m of mensajes) total += estimarTokensDeContenido(m.content) + 4;
  return total;
}

export function hayQueCompactar(mensajes: MensajeApi[], model: string | null | undefined): boolean {
  return estimarTokens(mensajes) > ventanaDelModelo(model) * FRACCION_UMBRAL;
}

/**
 * Índice del primer mensaje que SE QUEDA. `0` = no hay nada para resumir.
 *
 * Se elige el corte más TEMPRANO (o sea, la cola más larga) cuya cola entre en el
 * presupuesto, pero nunca uno tan tardío que deje menos de `minCola` mensajes: si el
 * presupuesto no alcanza ni para eso, gana la cola mínima y el turno se juega al recorte
 * de emergencia si el modelo igual se queja.
 */
export function puntoDeCorte(
  mensajes: MensajeApi[],
  objetivoTokens: number,
  minCola: number = MIN_MENSAJES_EN_COLA,
): number {
  const indicesUser: number[] = [];
  for (let i = 0; i < mensajes.length; i++) if (sirveDeCorte(mensajes[i])) indicesUser.push(i);
  if (indicesUser.length === 0) return 0;

  // Tope: más allá de este corte la cola quedaría por debajo del mínimo.
  let limite = indicesUser[indicesUser.length - 1];
  for (const u of indicesUser) {
    if (mensajes.length - u >= minCola) limite = u;
  }

  // Tokens acumulados desde el final, para saber cuánto pesa la cola de cada corte.
  const desde: number[] = new Array(mensajes.length + 1).fill(0);
  for (let i = mensajes.length - 1; i >= 0; i--) {
    desde[i] = desde[i + 1] + estimarTokensDeContenido(mensajes[i].content) + 4;
  }

  let corte = indicesUser[indicesUser.length - 1];
  for (const u of indicesUser) {
    if (desde[u] <= objetivoTokens) {
      corte = u;
      break;
    }
  }
  // El presupuesto puede pedir cortar más tarde que el mínimo de cola: ahí gana el mínimo.
  corte = Math.min(corte, limite);
  // Menos de dos intercambios en la cabeza no pagan el viaje al modelo.
  return corte < 4 ? 0 : corte;
}

/**
 * ¿Se puede empezar la cola por acá? Tiene que ser del usuario y NO ser la respuesta a
 * unas herramientas: un mensaje con `tool_result` sólo es válido pegado al `tool_use` que
 * lo pidió, y además la API exige que esos bloques vayan primero (o sea, no se les puede
 * anteponer el resumen). Aparecen cuando el recorte de emergencia corre en una ronda
 * avanzada del turno.
 */
function sirveDeCorte(m: MensajeApi): boolean {
  if (m.role !== 'user') return false;
  if (!Array.isArray(m.content)) return true;
  return !m.content.some((b) => b && typeof b === 'object' && (b as { type?: string }).type === 'tool_result');
}

const ROL = { user: 'Usuario', assistant: 'Agente' } as const;

/** El tramo a resumir, como transcripción plana. Las imágenes no viajan (el resumen es de
 *  texto): quedan nombradas para que el resumen sepa que existieron. */
export function transcripcionDelTramo(mensajes: MensajeApi[], hasta: number): string {
  const partes: string[] = [];
  for (let i = 0; i < Math.min(hasta, mensajes.length); i++) {
    const m = mensajes[i];
    partes.push(`${ROL[m.role]}: ${textoPlano(m.content)}`);
  }
  return partes.join('\n\n');
}

function textoPlano(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => {
        if (!b || typeof b !== 'object') return String(b ?? '');
        const bloque = b as Record<string, unknown>;
        if (bloque.type === 'image') return '[imagen]';
        if (typeof bloque.text === 'string') return bloque.text;
        if (bloque.type === 'tool_result') return textoPlano(bloque.content);
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (content == null) return '';
  return JSON.stringify(content);
}

/** Ids literales que aparecieron en un tramo: lo único que el recorte a lo bruto puede
 *  salvar sin entender nada de lo que se dijo. */
export function idsDelTexto(texto: string, tope = 60): string[] {
  const vistos = new Set<string>();
  // Los registros mezclan claves en inglés (las del schema de las tools) y en castellano
  // (las que arma `useV2Chat` para el historial), y algunas van entre corchetes.
  const campos =
    /\b(?:image_ids?|first_frame_image_id|last_frame_image_id|product_ids?|productos|model_id|modelo|pose_ids?|poses|lote_id|pdf_id|planilla_id|id)\s*=\s*\[?([A-Za-z0-9_-]+(?:\s*,\s*[A-Za-z0-9_-]+)*)\]?/g;
  for (const m of texto.matchAll(campos)) {
    for (const bruto of m[1].split(',')) {
      const id = bruto.trim();
      if (id.length >= 4) vistos.add(id);
      if (vistos.size >= tope) return [...vistos];
    }
  }
  return [...vistos];
}

const ENCABEZADO = '[NOTA DEL SISTEMA — RESUMEN DE LO ANTERIOR DE ESTE CHAT]';
const CIERRE = '[FIN DEL RESUMEN — de acá para abajo van los mensajes textuales]';

/** El resumen, envuelto para que el modelo sepa qué es: no son palabras del usuario, los
 *  ids sí son literales, y lo que falte se busca en vez de inventarse. */
export function bloqueDeResumen(resumen: string, mensajesResumidos: number): string {
  return [
    ENCABEZADO,
    `Esta conversación se hizo larga: los primeros ${mensajesResumidos} mensajes se resumieron para que entre en contexto.`,
    'Lo que sigue es un RESUMEN, no palabras textuales. Los identificadores son literales y se pueden usar tal cual.',
    'Si necesitás algo puntual de esa parte —qué se dijo, un id, un pedido, un rechazo— usá `buscar_en_el_chat`: el chat completo sigue guardado. No lo adivines ni le pidas al usuario que te lo repita.',
    '',
    resumen.trim(),
    '',
    CIERRE,
  ].join('\n');
}

/**
 * Plan B (el "1" de las opciones): sin modelo. Se tira el texto y se conservan los ids,
 * que es lo único que después no se puede reconstruir a mano.
 */
export function cuerpoDeEmergencia(transcripcion: string, mensajesResumidos: number): string {
  const ids = idsDelTexto(transcripcion);
  return [
    `No se pudo generar el resumen automático de los primeros ${mensajesResumidos} mensajes, así que se recortaron.`,
    ids.length > 0
      ? `Lo único que quedó de esa parte son los identificadores que aparecieron: ${ids.join(', ')}.`
      : 'De esa parte no quedó nada más que este aviso.',
    'Tratá esa parte como desconocida: si el usuario menciona algo de antes, buscalo con `buscar_en_el_chat` antes de responder.',
  ].join(' ');
}

export function resumenDeEmergencia(transcripcion: string, mensajesResumidos: number): string {
  return bloqueDeResumen(cuerpoDeEmergencia(transcripcion, mensajesResumidos), mensajesResumidos);
}

/**
 * Pliega `[0, corte)` en la nota y la pega ADELANTE del primer mensaje de la cola (que es
 * del usuario). Se pega en vez de agregarse como mensaje aparte para no dejar dos `user`
 * seguidos, que el adapter de Gemini no espera. Muta el array: el body del request lo
 * referencia por identidad.
 */
export function aplicarCompactacion(mensajes: MensajeApi[], corte: number, nota: string): void {
  if (corte <= 0 || corte >= mensajes.length) return;
  const primero = mensajes[corte];
  const content =
    typeof primero.content === 'string'
      ? `${nota}\n\n${primero.content}`
      : [
          { type: 'text', text: nota },
          ...(Array.isArray(primero.content) ? primero.content : [primero.content]),
        ];
  mensajes.splice(0, corte + 1, { ...primero, content });
}

/**
 * Lo que se le suma al bloque VOLÁTIL del system prompt cuando este chat ya se compactó.
 * Va acá y no en el MAPA (estable y cacheado) porque sólo aplica a los chats largos: el
 * 99% de las conversaciones nunca llega a leerlo.
 */
export function avisoDeChatCompactado(mensajesResumidos: number): string {
  return [
    '',
    '══════════ ESTE CHAT ES LARGO ══════════',
    `Se resumieron los primeros ${mensajesResumidos} mensajes: en vez del texto original vas a encontrar, arriba de todo, una NOTA DEL SISTEMA con el resumen.`,
    'El chat completo sigue guardado. Si el usuario da algo por dicho y en el resumen no está —qué pidió exactamente, un número, un nombre, un id, algo que descartó—, buscalo con `buscar_en_el_chat` antes de contestar.',
    'No le pidas que te repita lo que ya te dijo, y no des por hecho lo que no leíste.',
  ].join('\n');
}
